// components/SessionExpiredModal.jsx
// Blocking takeover shown when utils/fetchInterceptor detects that the Azure
// SWA auth cookie has died — the edge starts returning an HTML 401 for every
// /api/* call and the Functions never run.
//
// Lives here rather than scheduling/components/ because it's a shell/auth
// concern: it can fire on /reassign/* just as easily as on the scheduler.
//
// Unlike SettingsModal / OutlookAnnounceModal this dialog is NOT dismissible —
// no × button, no backdrop click. Once the session is dead nothing can save, so
// letting someone keep typing only costs them more work. That's also why it
// carries ARIA the other two don't need: a screen-reader user must not be able
// to tab past it into controls that no longer function.

import { useEffect, useRef } from 'react';
import { isLocalDev, redirectToAAD } from '../auth/AuthContext';

export default function SessionExpiredModal({ open }) {
  const primaryRef = useRef(null);

  useEffect(() => {
    if (open) primaryRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const handleSignIn = () => {
    // redirectToAAD no-ops in local dev (the SWA CLI owns auth there), so give
    // the button something to do rather than leaving it visibly dead.
    if (isLocalDev()) { window.location.reload(); return; }
    redirectToAAD();
  };

  // Only two focusable elements and no way out of the dialog — bounce Tab back
  // to the primary action instead of pulling in a focus-trap library.
  const handleKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    primaryRef.current?.focus();
  };

  return (
    <div className="app-modal-overlay app-session-expired-overlay">
      <div
        className="app-modal app-session-expired-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="app-session-expired-title"
        aria-describedby="app-session-expired-body"
        onKeyDown={handleKeyDown}
      >
        <h2 id="app-session-expired-title" className="app-modal-title">You've been signed out</h2>
        <div id="app-session-expired-body" className="app-session-expired-body">
          <p>
            For your security, you get signed out automatically after several hours —
            even if you left this tab open.
          </p>
          <p>
            Signing back in will reload the page, so if you were in the middle of typing
            something, please copy it somewhere safe first — it won't be saved.
          </p>
        </div>
        <div className="app-session-expired-actions">
          <button
            ref={primaryRef}
            className="app-settings-btn app-settings-btn--primary"
            onClick={handleSignIn}
          >
            Sign back in
          </button>
          <a className="app-session-expired-secondary" href="/.auth/logout">
            Not you? Sign out
          </a>
        </div>
      </div>
    </div>
  );
}
